import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as auth0 from 'auth0-js';
import { jwtDecode } from 'jwt-decode';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import moment from 'moment';
import { IdentityProvider } from '~models';
import { AuthService } from '~services/auth/auth.service';

const config = {
  domain: environment.zipMobileSSO.domain,
  clientID: environment.zipMobileSSO.seamlessLoginPageClientId,
  redirectUri: `${window.location.origin}/au/giftcards/auth/mobilesso`,
  response_type: 'id_token',
  responseType: 'id_token',
};

@Injectable({
  providedIn: 'root',
})
export class MobileSSOService {
  constructor(
    private router: Router,
    private authService: AuthService,
    private cookieService: CookieService,
  ) {}

  private setAuthCookies(idToken: string): void {
    try {
      // Decode the JWT to extract claims
      const decodedToken = jwtDecode(idToken);

      const cookieOptions = {
        domain: `.${window.location.hostname}`,
        path: '/',
        secure: true,
        expires: moment().add({ years: 1 }).toDate(),
      };

      // Check if the issuer is from the expected Auth0 domain
      if (decodedToken.iss?.includes(environment.zipMobileSSO.domain)) {
        this.cookieService.set('idp_cookie', IdentityProvider.Okta, cookieOptions);
      }

      const customerId = decodedToken['customer_id'];
      // Check if the 'customer_id' claim exists and set the cookie
      if (customerId) {
        this.cookieService.set('zip_id', customerId, cookieOptions);
      }
    } catch (error) {
      window.zbm.reportCustomError(
        'AUTH0_MOBILE_SSO_TOKEN_DECODE_FAILED',
        error?.message,
        this.cookieService.get('zip_id'),
      );
    }
  }

  public handleMobileAppCallback(data: MobileCallbackData) {
    localStorage.setItem('targetUri', data.redirectUri);

    try {
      const userId = data.userId;
      const accessToken = data.accessToken;

      const webAuth = new auth0.WebAuth(config);
      webAuth.login(
        {
          email: userId,
          password: accessToken,
          realm: 'Mobile-SSO-Token',
        },
        (err) => {
          window.zbm.reportCustomError(
            'AUTH0_MOBILE_SSO_CALLBACK_FAILED',
            err.error,
            this.cookieService.get('zip_id'),
          );
        },
      );
    } catch (e) {
      if (e instanceof Error) {
        window.zbm.reportCustomError(
          'AUTH0_MOBILE_SSO_CALLBACK_FAILED',
          e.message,
          this.cookieService.get('zip_id'),
        );
      }
      throw e;
    }
  }

  handleSSOComplete() {
    const url = localStorage.getItem('targetUri');

    const webAuth = new auth0.WebAuth(config);
    webAuth.parseHash(async (parseErr, parsedResult) => {
      if (parseErr) {
        window.zbm.reportCustomError(
          'AUTH0_MOBILE_SSO_PARSE_HASH_FAILED',
          parseErr.error,
          this.cookieService.get('zip_id'),
        );
        return;
      }
      if (parsedResult && parsedResult.idToken) {
        const idToken = parsedResult.idToken;

        this.setAuthCookies(idToken);

        await this.authService.initialise();
        this.router.navigateByUrl(url);
      }
    });
  }
}

export type MobileCallbackData = {
  redirectUri: string;
  userId: string;
  accessToken: string;
};

declare global {
  interface Window {
    callbackFromMobileApp?: (json: MobileCallbackData) => void;
  }
}
